/**
 * The ONE household connection a Lantern tab holds (#580, live updates PR 1).
 *
 * It is `createBoardSocket` — the listen-only adapter the shared display already uses — owned by a
 * hook, so a screen never constructs a socket of its own. The legacy client's
 * `src/hooks/useWebSocket.js` is a different module and is deliberately NOT imported: it is a
 * singleton built on `socket.io-client`, which this app does not depend on.
 *
 * One connection per `authority`. The string names who the connection is for, and changes when the
 * signed-in person does — including the same person signing in again. A new authority closes the
 * old socket BEFORE the new one opens, and the old socket's late events are dropped, because
 * `close()` clears its listeners and `current` is false. `null` means "nobody a household socket
 * may speak for" (signed out, a shared display, a platform operator), and no socket is opened.
 *
 * `install(socket)` runs synchronously after the socket is created, which is BEFORE it can join:
 * joining needs two server packets, and they arrive on later tasks. So no event can land before
 * its listener exists. `onJoin()` runs on every join, first and reconnect alike, and is where the
 * caller re-reads — a read made before the join cannot prove nothing changed in between.
 *
 * ponytail: websocket transport only, inherited from `boardSocket.js`. A proxy that blocks
 * websockets leaves `joined` false for good, and the caller's 30-second re-read is then the only
 * path; that is shown as such, never as live.
 */
import { useEffect, useRef, useState } from 'react';

import { createBoardSocket } from '../api/boardSocket.js';

const IDLE = Object.freeze({ authority: null, joined: false });

/**
 * @param {object} options
 * @param {string|null} options.authority
 * @param {(socket: ReturnType<typeof createBoardSocket>) => void} options.install
 * @param {() => void} [options.onJoin]
 * @param {typeof createBoardSocket} [options.socketFactory]
 * @returns {{ authority: string|null, joined: boolean }}
 */
export function useWebSocket({ authority, install, onJoin, socketFactory = createBoardSocket }) {
  const [link, setLink] = useState(IDLE);
  const hooks = useRef({ install, onJoin });
  hooks.current = { install, onJoin };

  useEffect(() => {
    if (!authority) return undefined;
    let current = true;
    const socket = socketFactory();
    const leave = () => {
      if (!current) return;
      setLink({ authority, joined: false });
    };
    socket.on('connect', () => {
      if (!current) return;
      setLink({ authority, joined: true });
      hooks.current.onJoin?.();
    });
    socket.on('disconnect', leave);
    socket.on('connect_error', leave);
    hooks.current.install(socket);
    return () => {
      current = false;
      socket.close();
    };
  }, [authority, socketFactory]);

  // A link reported for an earlier authority says nothing about this one.
  return link.authority === authority && authority ? link : { authority: authority ?? null, joined: false };
}
